import { useState } from 'react';

interface Props {
  onClose: () => void;
  level: 'GCSE' | 'A-Level';
}

type Step = 'config' | 'generating' | 'ready';

const gcseTopics = [
  'Algorithms', 'Programming Fundamentals', 'Data Structures', 'Computer Systems',
  'Networks & Cyber Security', 'Data Representation', 'Boolean Logic', 'CPU Architecture',
  'Software Development', 'Ethical & Legal Issues', 'Searching & Sorting', 'Recursion',
];

const alevelTopics = [
  'Computational Thinking', 'Problem Solving', 'Data Structures & Algorithms',
  'Theory of Computation', 'Fundamentals of Data Representation', 'Computer Systems',
  'Fundamentals of Computer Networks', 'Databases', 'Big Data', 'Functional Programming',
  'Object-Oriented Programming', 'Cyber Security', 'Artificial Intelligence', 'Legal & Ethical Issues',
];

const gcseBoards = ['AQA', 'OCR', 'Edexcel', 'WJEC'];
const alevelBoards = ['AQA', 'OCR'];

export default function AiGeneratorModal({ onClose, level }: Props) {
  const [step, setStep] = useState<Step>('config');
  const [board, setBoard] = useState('AQA');
  const [difficulty, setDifficulty] = useState('Mixed');
  const [numQuestions, setNumQuestions] = useState(20);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [paperName, setPaperName] = useState('');
  const [progress, setProgress] = useState(0);

  const topics = level === 'GCSE' ? gcseTopics : alevelTopics;
  const boards = level === 'GCSE' ? gcseBoards : alevelBoards;

  const toggleTopic = (t: string) => {
    setSelectedTopics((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const handleGenerate = () => {
    setStep('generating');
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(interval);
          setStep('ready');
          return 100;
        }
        return p + Math.random() * 18;
      });
    }, 300);
  };

  const generatingMessages = [
    'Analysing exam board specification...',
    'Selecting topic-appropriate questions...',
    'Calibrating difficulty level...',
    'Formatting exam paper layout...',
    'Generating mark scheme...',
    'Finalising your paper...',
  ];

  const msgIndex = Math.min(Math.floor(progress / 17), generatingMessages.length - 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl">
              <i className="ri-sparkling-2-line text-emerald-600 text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">AI Paper Generator</h2>
              <p className="text-xs text-slate-400">{level} Computer Science</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-slate-500 text-lg"></i>
          </button>
        </div>

        {/* Config Step */}
        {step === 'config' && (
          <div className="px-6 py-6 space-y-6">
            {/* Paper Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Paper Name (optional)</label>
              <input
                type="text"
                value={paperName}
                onChange={(e) => setPaperName(e.target.value)}
                placeholder={`e.g. My ${level} Mock — Paper 1`}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 transition-colors"
              />
            </div>

            {/* Board + Difficulty */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Exam Board</label>
                <div className="flex flex-wrap gap-2">
                  {boards.map((b) => (
                    <button
                      key={b}
                      onClick={() => setBoard(b)}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                        board === b ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Difficulty</label>
                <div className="flex flex-wrap gap-2">
                  {['Foundation', 'Mixed', 'Higher'].map((d) => (
                    <button
                      key={d}
                      onClick={() => setDifficulty(d)}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                        difficulty === d ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Number of Questions */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Number of Questions — <span className="text-slate-800">{numQuestions}</span>
              </label>
              <input
                type="range"
                min={5}
                max={40}
                step={5}
                value={numQuestions}
                onChange={(e) => setNumQuestions(Number(e.target.value))}
                className="w-full accent-emerald-600 cursor-pointer"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>5</span><span>10</span><span>15</span><span>20</span><span>25</span><span>30</span><span>35</span><span>40</span>
              </div>
            </div>

            {/* Topics */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Topics to Include
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTopics([...topics])}
                    className="text-xs text-emerald-600 font-medium hover:text-emerald-700 cursor-pointer"
                  >
                    Select all
                  </button>
                  <span className="text-slate-300">·</span>
                  <button
                    onClick={() => setSelectedTopics([])}
                    className="text-xs text-slate-400 font-medium hover:text-slate-600 cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-3">Leave empty to include all topics automatically.</p>
              <div className="flex flex-wrap gap-2">
                {topics.map((t) => (
                  <button
                    key={t}
                    onClick={() => toggleTopic(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      selectedTopics.includes(t)
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {selectedTopics.includes(t) && <i className="ri-check-line mr-1 text-xs"></i>}
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Paper Summary</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700">
                <span><span className="text-slate-400 text-xs">Board</span> <strong>{board}</strong></span>
                <span><span className="text-slate-400 text-xs">Level</span> <strong>{level}</strong></span>
                <span><span className="text-slate-400 text-xs">Difficulty</span> <strong>{difficulty}</strong></span>
                <span><span className="text-slate-400 text-xs">Questions</span> <strong>{numQuestions}</strong></span>
                <span><span className="text-slate-400 text-xs">Topics</span> <strong>{selectedTopics.length === 0 ? 'All' : selectedTopics.length + ' selected'}</strong></span>
              </div>
            </div>

            <button
              onClick={handleGenerate}
              className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors cursor-pointer"
            >
              <i className="ri-sparkling-line text-base"></i>
              Generate My Paper
            </button>
          </div>
        )}

        {/* Generating Step */}
        {step === 'generating' && (
          <div className="px-6 py-12 flex flex-col items-center text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mb-6">
              <i className="ri-sparkling-2-line text-emerald-600 text-3xl animate-pulse"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Generating Your Paper</h3>
            <p className="text-sm text-slate-500 mb-8 min-h-[20px]">{generatingMessages[msgIndex]}</p>
            <div className="w-full max-w-sm bg-slate-100 rounded-full h-2 mb-3">
              <div
                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(progress, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-slate-400">{Math.min(Math.round(progress), 100)}% complete</p>
          </div>
        )}

        {/* Ready Step */}
        {step === 'ready' && (
          <div className="px-6 py-8">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mb-4">
                <i className="ri-checkbox-circle-line text-emerald-600 text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Your Paper is Ready!</h3>
              <p className="text-sm text-slate-500">
                {paperName || `${level} ${board} Mock Exam`} — {numQuestions} questions · {difficulty}
              </p>
            </div>

            {/* Paper Preview */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 mb-6 font-mono text-xs text-slate-700 leading-relaxed">
              <div className="text-center mb-4 pb-4 border-b border-slate-200">
                <p className="font-bold text-sm text-slate-900">{board} {level} Computer Science</p>
                <p className="text-slate-500">{paperName || 'AI Generated Mock Exam'}</p>
                <p className="text-slate-400 mt-1">Time allowed: {Math.round(numQuestions * 2.5)} minutes &nbsp;·&nbsp; {numQuestions * 4} marks</p>
              </div>
              <p className="font-bold text-slate-800 mb-2">Section A — Short Answer Questions</p>
              <p className="text-slate-600 mb-1">1. Define the term &apos;algorithm&apos; and give one example of its use in computing. <span className="text-slate-400">[2 marks]</span></p>
              <p className="text-slate-600 mb-1">2. Explain the difference between RAM and ROM. <span className="text-slate-400">[2 marks]</span></p>
              <p className="text-slate-600 mb-3">3. Convert the binary number 10110101 to denary. <span className="text-slate-400">[2 marks]</span></p>
              <p className="font-bold text-slate-800 mb-2">Section B — Extended Answer Questions</p>
              <p className="text-slate-600 mb-1">4. Describe how a bubble sort algorithm works. Include a worked example. <span className="text-slate-400">[6 marks]</span></p>
              <p className="text-slate-400 text-center mt-4">· · · {numQuestions - 4} more questions · · ·</p>
            </div>

            <div className="flex gap-3">
              <button className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors cursor-pointer">
                <i className="ri-download-line text-sm"></i>
                Download PDF
              </button>
              <button className="flex-1 flex items-center justify-center gap-2 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-colors cursor-pointer">
                <i className="ri-file-copy-line text-sm"></i>
                Download Mark Scheme
              </button>
            </div>
            <button
              onClick={() => setStep('config')}
              className="w-full mt-3 text-sm text-slate-400 hover:text-slate-600 transition-colors cursor-pointer py-2"
            >
              Generate another paper
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
